"use client";

import type { HTMLAttributes } from "react";
import { forwardRef, useState } from "react";
import { Action } from "../../action";
import { Button } from "../../button";
import { useConstant } from "../../hooks/use-constant";
import { InputLabel, Wrapper } from "../../input";
import { HStack } from "../../layout/stack";
import { cn } from "../../lib/utils";
import { Tag } from "../../tag";
import { Disclaimer } from "../../typography";

interface SubmitInputProps {
	text: string;
	disclaimer?: string;
	disabled?: boolean;
}

export const WidgetSubmitInput = forwardRef<
	HTMLInputElement,
	SubmitInputProps & HTMLAttributes<HTMLInputElement>
>(({ text, disclaimer, ...props }, ref) => {
	return (
		<>
			<Button asChild>
				<input
					aria-label="submit"
					ref={ref}
					type="submit"
					value={text}
					{...props}
				/>
			</Button>
			{disclaimer ? <Disclaimer>{disclaimer}</Disclaimer> : null}
		</>
	);
});

WidgetSubmitInput.displayName = "WidgetSubmitInput";

export interface WidgetInputProps {
	name: string;
	defaultValue?: string;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	value?: string;
	required?: boolean;
	type?: HTMLInputElement["type"];
}

export const WidgetInput = forwardRef<
	HTMLInputElement,
	WidgetInputProps & HTMLAttributes<HTMLInputElement>
>(
	(
		{
			name,
			label,
			type = "text",
			required,
			placeholder,
			onChange,
			onBlur,
			...props
		},
		ref,
	): React.ReactElement => {
		return (
			<Wrapper>
				{label ? <InputLabel required={required} text={label} /> : null}
				<input
					aria-label={name}
					className={cn(
						"text-md text-input-text",
						"placeholder:text-input-text-placeholder",
						"disabled:text-input-text-disabled",
						"bg-transparent",
						"outline-none",
					)}
					name={name}
					onBlur={onBlur}
					onChange={onChange}
					placeholder={placeholder}
					ref={ref}
					required={required}
					type={type}
					{...props}
				/>
			</Wrapper>
		);
	},
);

WidgetInput.displayName = "WidgetInput";

export const SecretInput = forwardRef<
	HTMLInputElement,
	WidgetInputProps & HTMLAttributes<HTMLInputElement>
>(
	(
		{ name, label, required, placeholder, onBlur, onChange, ...props },
		ref,
	): React.ReactElement => {
		const [reveal, setReveal] = useState(false);

		const toggleReveal = (): void => {
			setReveal((prev) => !prev);
		};

		return (
			<Wrapper>
				{label ? <InputLabel required={required} text={label} /> : null}
				<HStack className="relative">
					<input
						aria-label={name}
						className={cn(
							"w-full",
							"text-md text-input-text",
							"placeholder:text-input-text-placeholder",
							"bg-transparent",
							"outline-none",
						)}
						name={name}
						onBlur={onBlur}
						onChange={onChange}
						placeholder={placeholder}
						ref={ref}
						required={required}
						type={reveal ? "text" : "password"}
						{...props}
					/>
					<Action
						className="-translate-y-1/2 absolute top-1/2 right-0 translate-x-[12px]"
						icon={reveal ? "eye-slash" : "eye"}
						onClick={toggleReveal}
					/>
				</HStack>
			</Wrapper>
		);
	},
);

SecretInput.displayName = "SecretInput";

export interface WidgetSelectInputProps {
	options: string[];
	name: string;
	handleSelect: (name: string, values: string[]) => void;
	required?: boolean;
	multiple?: boolean;
	label?: string;
}

export const WidgetSelectInput = forwardRef<
	HTMLDivElement,
	WidgetSelectInputProps
>(({ label, options, name, handleSelect, ...props }, ref) => {
	const selected = useConstant<Set<string>>(() => new Set());

	const onSelect = (value: string): void => {
		if (selected.has(value)) {
			selected.delete(value);
		} else {
			selected.add(value);
		}

		handleSelect(name, Array.from(selected));
	};

	return (
		<Wrapper>
			{label ? <InputLabel text={label} /> : null}
			<HStack
				aria-label={name}
				className="flex-wrap gap-xs pt-xs"
				ref={ref}
				{...props}
			>
				{options.map((o) => (
					<Tag
						className="whitespace-nowrap"
						key={o}
						name={o}
						onSelect={onSelect}
						prefix={label}
					/>
				))}
			</HStack>
		</Wrapper>
	);
});

WidgetSelectInput.displayName = "WidgetSelectInput";

export function WidgetSuccessMessage({
	message,
}: {
	message: string;
}): React.ReactElement {
	return <div className="text-success">{message}</div>;
}
